import type {
    MyDocumentsSummary,
    MyDocumentsSummaryByCurrency,
} from "@/modules/quotation-invoice/domain/types";
import { DollarSign, FileText, Receipt } from "lucide-react";

type Props = {
  data: MyDocumentsSummary | undefined;
  isLoading?: boolean;
};

function CurrencyBreakdownList({
  rows,
  kind,
}: {
  rows: MyDocumentsSummaryByCurrency[];
  kind: "quotation" | "invoice" | "receipt";
}) {
  return (
    <ul className="mt-3 space-y-3">
      {rows.map((row) => {
        const cur = row.currency;
        if (kind === "quotation") {
          const q = row.byDocumentType.quotationIssued;
          return (
            <li
              key={cur}
              className="border-border/80 border-b pb-3 last:border-0 last:pb-0"
            >
              <p className="font-mono text-muted-foreground text-xs">{cur}</p>
              <p className="font-bold text-2xl tabular-nums">{q.count}</p>
              <p className="text-muted-foreground text-xs">
                ≈ {q.total.toLocaleString()} {cur}
              </p>
            </li>
          );
        }
        if (kind === "invoice") {
          const inv = row.byDocumentType.invoiceOpen;
          return (
            <li
              key={cur}
              className="border-border/80 border-b pb-3 last:border-0 last:pb-0"
            >
              <p className="font-mono text-muted-foreground text-xs">{cur}</p>
              <p className="font-bold text-2xl tabular-nums">{inv.count}</p>
              <p className="text-muted-foreground text-xs">
                ≈ {inv.outstandingTotal.toLocaleString()} {cur}
              </p>
            </li>
          );
        }
        const r = row.byDocumentType.receiptAll;
        return (
          <li
            key={cur}
            className="border-border/80 border-b pb-3 last:border-0 last:pb-0"
          >
            <p className="font-mono text-muted-foreground text-xs">{cur}</p>
            <p className="font-bold text-2xl tabular-nums">{r.count}</p>
            <p className="text-muted-foreground text-xs">
              ≈ {r.total.toLocaleString()} {cur}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

/** ສະຫຼຸບຕາມປະເພດ — ທຸກສະກຸນເງິນໃນ card ດຽວກັນ */
export function MyDocumentsSummaryCards({ data, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="animate-pulse rounded-xl border bg-card p-6">
        <div className="grid gap-6 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <div className="h-4 w-32 rounded bg-muted" />
              <div className="mt-4 h-20 w-full rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const rows: MyDocumentsSummaryByCurrency[] =
    data?.byCurrency && data.byCurrency.length > 0
      ? data.byCurrency
      : data
        ? [
            {
              currency: "ລວມ",
              draft: data.draft,
              byDocumentType: data.byDocumentType,
            },
          ]
        : [];

  if (!data || rows.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນສະຫຼຸບ</p>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-5 md:p-6">
      <div className="grid gap-8 md:grid-cols-3 md:gap-6">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileText className="h-4 w-4 shrink-0 text-slate-500" />
            <span className="font-medium text-sm">ໃບສະເໜີ (ອອກແລ້ວ)</span>
          </div>
          <CurrencyBreakdownList rows={rows} kind="quotation" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <DollarSign className="h-4 w-4 shrink-0 text-amber-500" />
            <span className="font-medium text-sm">ໃບເກັບເງິນ (ຄ້າງຊຳລະ)</span>
          </div>
          <CurrencyBreakdownList rows={rows} kind="invoice" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Receipt className="h-4 w-4 shrink-0 text-emerald-500" />
            <span className="font-medium text-sm">ໃບຮັບເງິນ</span>
          </div>
          <CurrencyBreakdownList rows={rows} kind="receipt" />
        </div>
      </div>
    </div>
  );
}
